import React, {FC, useCallback, useMemo} from 'react';
import { Trans } from '@ui/i18n/trans'

import { IconButton } from '@ui/buttons/icon-button'
import { PlaylistAddIcon } from '@ui/icons/material/PlaylistAdd'

import { Track } from '@player/types';
import { Tooltip } from '@ui/tooltip/tooltip'

import { DialogTrigger } from '@ui/overlays/dialog/dialog-trigger'
import { Dialog } from '@ui/overlays/dialog/dialog'

import { DialogBody } from '@ui/overlays/dialog/dialog-body'
import { useAuthUserPlaylists } from '@app/web-player/playlists/requests/use-auth-user-playlists'

import { useAuth } from '@common/auth/use-auth'
import { ContextMenuButton } from '@app/web-player/context-dialog/context-dialog-layout'

import { useAddTracksToPlaylist } from '@app/web-player/playlists/requests/use-add-tracks-to-playlist'
import { AddIcon } from '@ui/icons/material/Add'

import { openDialog } from '@ui/overlays/store/dialog-store'
import { CreatePlaylistDialog } from '@app/web-player/playlists/crupdate-dialog/create-playlist-dialog'

import { toast } from '@ui/toast/toast'
import { message } from '@ui/i18n/message'

import { useDialogContext } from '@ui/overlays/dialog/dialog-context'

export interface AddToLibraryButtonProps {
    track: Track
}

export const AddToLibraryButton: FC<AddToLibraryButtonProps> = ({ track }) => {
    return (
        <DialogTrigger type="popover" mobileType="tray">
            <Tooltip delay={0} label={<Trans message="Add to playlist" />}>
                <IconButton
                    className="h-32 w-32"
                >
                    <PlaylistAddIcon className="h-24 w-24" />
                </IconButton>
            </Tooltip>

            <AddToLibraryDialog track={track} />
        </DialogTrigger>
    )
}


export const AddToLibraryDialog: FC<AddToLibraryButtonProps> = ({ track }) => {
    const { data } = useAuthUserPlaylists()
    const { user } = useAuth()

    const { close: closeMenu } = useDialogContext()

    const tracks = useMemo(() => {
        return [track]
    }, [track])
    const loadTracks = useCallback(() => {
        return Promise.resolve(tracks)
    }, tracks)

    const addToPlaylist = useAddTracksToPlaylist()
    const playlists = useMemo(() => {
        return data.playlists.filter(
            p => p.owner_id === user?.id || p.collaborative
        )
    }, [data, user])

    return (
        <Dialog size="xs">
            <DialogBody
                padding="p-0"
            >
                <div className="pb-0">
                    <ul className="max-h-350 overflow-y-auto overflow-x-hidden">
                        <ContextMenuButton
                            startIcon={<AddIcon />}
                            onClick={async () => {
                                closeMenu()

                                const [playlist, tracks] = await Promise.all([
                                    openDialog(CreatePlaylistDialog),
                                    loadTracks(),
                                ]);
                                if (tracks.length && playlist) {
                                    addToPlaylist.mutate({
                                        playlistId: playlist.id,
                                        tracks,
                                    });
                                }
                            }}
                            className="text-primary"
                        >
                            <Trans message="New playlist" />
                        </ContextMenuButton>
                        {playlists.map(playlist => (
                            <ContextMenuButton
                                key={playlist.id}
                                onClick={async () => {
                                    closeMenu()

                                    const tracks = await loadTracks();
                                    if (tracks?.length && !addToPlaylist.isPending) {
                                        addToPlaylist.mutate({
                                            playlistId: playlist.id,
                                            tracks,
                                        });
                                    } else {
                                        toast(message('This item does not have tracks yet'));
                                    }
                                }}
                            >
                                {playlist.name}
                            </ContextMenuButton>
                        ))}
                    </ul>
                </div>
            </DialogBody>
        </Dialog>
    )
}
